import {
  ConfirmationMessageTypeEnum,
  DataMessageTypeEnum,
  type ToolCallGenericMessage,
} from '@terra/shared-dto-chat-types';
import type {
  ProcessedMessage,
  ProcessedPart,
} from '../utils/message-processing';
import { getStableKeyForRenderItem } from '../utils/chat-render-item-keys';
import type {
  ChatRenderItem,
  RenderablePart,
} from './chat-message-list-grouping';
import { hasExecutedResultForActions } from './HITL/hitl-card.utils';

type ToolResultsById = Record<string, ToolCallGenericMessage>;

export function getHITLPartId(
  processedPart: ProcessedPart,
): string | undefined {
  const dataMessage = processedPart.dataMessage;

  if (
    dataMessage?.type !== ConfirmationMessageTypeEnum.HITLRequestConfirmation
  ) {
    return undefined;
  }

  return dataMessage.id ?? processedPart.messagePartId;
}

export function isPendingSingleActionToolHITLPart({
  processedPart,
  toolResultsById,
}: {
  processedPart: ProcessedPart;
  toolResultsById?: ToolResultsById;
}): boolean {
  const dataMessage = processedPart.dataMessage;

  if (
    processedPart.kind !== 'data-message' ||
    dataMessage?.type !== ConfirmationMessageTypeEnum.HITLRequestConfirmation
  ) {
    return false;
  }

  const actionRequests =
    dataMessage.hitlRequestConfirmationPrompt?.actionRequests ?? [];
  const actionRequest = actionRequests[0];

  return (
    !processedPart.selectedDecision &&
    !processedPart.hitlDecision &&
    actionRequests.length === 1 &&
    !!(actionRequest?.toolCallId ?? actionRequest?.id) &&
    !hasExecutedResultForActions({ actionRequests, toolResultsById })
  );
}

function backgroundSubagentToolCallId(
  processedPart: ProcessedPart,
): string | undefined {
  const dataMessage = processedPart.dataMessage;

  if (
    dataMessage?.type !== ConfirmationMessageTypeEnum.HITLRequestConfirmation
  ) {
    return undefined;
  }

  return dataMessage.backgroundSubagent?.toolCallId;
}

export function getPendingSingleActionHITLPartIdsFromParts({
  processedParts,
  toolResultsById,
}: {
  processedParts: ProcessedPart[];
  toolResultsById?: ToolResultsById;
}): string[] {
  const pendingHITLParts = processedParts.filter((processedPart) =>
    isPendingSingleActionToolHITLPart({ processedPart, toolResultsById }),
  );
  const batchedIds = new Set<string>();
  const foregroundParts = pendingHITLParts.filter(
    (processedPart) => !backgroundSubagentToolCallId(processedPart),
  );

  if (foregroundParts.length > 1) {
    foregroundParts.forEach((processedPart) => {
      const id = getHITLPartId(processedPart);

      if (id) {
        batchedIds.add(id);
      }
    });
  }

  const backgroundGroups = new Map<string, ProcessedPart[]>();

  pendingHITLParts.forEach((processedPart) => {
    const parentToolCallId = backgroundSubagentToolCallId(processedPart);

    if (!parentToolCallId) {
      return;
    }

    const group = backgroundGroups.get(parentToolCallId) ?? [];

    group.push(processedPart);
    backgroundGroups.set(parentToolCallId, group);
  });

  backgroundGroups.forEach((group) => {
    if (group.length <= 1) {
      return;
    }

    group.forEach((processedPart) => {
      const id = getHITLPartId(processedPart);

      if (id) {
        batchedIds.add(id);
      }
    });
  });

  return pendingHITLParts
    .map(getHITLPartId)
    .filter((id): id is string => !!id && batchedIds.has(id));
}

export function getPendingSingleActionHITLPartIds({
  processedMessage,
  toolResultsById,
}: {
  processedMessage: ProcessedMessage;
  toolResultsById?: ToolResultsById;
}): string[] {
  return getPendingSingleActionHITLPartIdsFromParts({
    processedParts: processedMessage.parts,
    toolResultsById,
  });
}

export function getPendingSingleActionHITLPartIdsFromItems({
  items,
  toolResultsById,
}: {
  items: RenderablePart[];
  toolResultsById?: ToolResultsById;
}): string[] {
  return getPendingSingleActionHITLPartIdsFromParts({
    processedParts: items.map((item) => item.processedPart),
    toolResultsById,
  });
}

function isCopilotPlanRenderItem(item: ChatRenderItem): boolean {
  return (
    item.kind === 'part' &&
    item.item.processedPart.dataMessage?.type ===
      DataMessageTypeEnum.CopilotPlan
  );
}

export function withPinnedPlanHeader(
  items: ChatRenderItem[],
): ChatRenderItem[] {
  const planItemIndex = items.reduce(
    (lastIndex, item, index) =>
      isCopilotPlanRenderItem(item) ? index : lastIndex,
    -1,
  );

  if (planItemIndex < 0) {
    return items;
  }

  return [
    ...items.slice(0, planItemIndex + 1),
    {
      kind: 'pinned-plan-header',
      key: `pinned-plan-${getStableKeyForRenderItem(items[planItemIndex])}`,
    },
    ...items.slice(planItemIndex + 1),
  ];
}
